import React from 'react';
import './StepperInput.css';
import Button from '../Button/Button';

interface StepperInputProps {
    min: number;
    max: number;
    step: number;
    isDisabled?: boolean;
    value?: number | null;
    onChange: (value: number | null) => void;
    placeholder?: string;
    inputWidth?: string;
}

const StepperInput: React.FC<StepperInputProps> = ({
    min,
    max,
    step,
    isDisabled = false,
    value,
    onChange,
    placeholder = 'Enter value',
    inputWidth
}) => {
    const parsedValue = value ?? null;

    const handleIncrement = () => {
        const base = parsedValue ?? min - step;
        const next = base + step;
        if (next <= max) onChange(next);
    };

    const handleDecrement = () => {
        const base = parsedValue ?? min + step;
        const next = base - step;
        if (next >= min) onChange(next);
    };

    const isIncrementDisabled = isDisabled || ((parsedValue ?? min - step) + step > max);
    const isDecrementDisabled = isDisabled || ((parsedValue ?? min + step) - step < min);

    return (
        <div className={`StepperInput ${isDisabled ? 'StepperInput--disabled' : ''}`}>
            <Button
                className="StepperInput__button StepperInput__button--minus"
                onClick={handleDecrement}
                disabled={isDecrementDisabled}
                icon="Minus"
                size="medium"
                buttonType="tertiary-outline"
                iconProps={{ size: 'original' }}
                onlyIcon={true}
            />

            <input
                name="number-input"
                type="number"
                className="StepperInput__input"
                value={parsedValue === null ? '' : parsedValue}
                inputMode="numeric"
                placeholder={placeholder}
                onChange={(e) => {
                    const input = e.target.value.trim();

                    // Strip non-digits
                    const cleaned = input.replace(/\D/g, '');
                    if (cleaned === '') {
                        onChange(null);
                        return;
                    }

                    const parsed = parseInt(cleaned, 10);
                    if (!isNaN(parsed) && parsed >= min && parsed <= max) {
                        onChange(parsed);
                    }
                }}
                onKeyDown={(e) => {
                    // Allow: Backspace, Delete, Tab, Enter, Arrow keys, cmd/ctrl + A, cmd/ctrl + C, cmd/ctrl + V
                    if (
                        e.key.length === 1 &&
                        !/[0-9]/.test(e.key) &&
                        !(e.metaKey && (e.key === 'a' ||
                            e.key === 'c' ||
                            e.key === 'v'))
                    ) {
                        e.preventDefault();
                    }
                }}
                onPaste={(e) => {
                    // Filter pasted content to only include numbers
                    e.preventDefault();

                    const pasted = e.clipboardData.getData('text/plain');
                    const cleaned = pasted.replace(/\D/g, '');

                    // Insert cleaned text at the current caret
                    document.execCommand('insertText', false, cleaned);
                }}
                style={{ width: inputWidth || 'auto' }}
                disabled={isDisabled}
            />


            <Button
                className="StepperInput__button StepperInput__button--plus"
                onClick={handleIncrement}
                disabled={isIncrementDisabled}
                icon="Plus"
                iconProps={{ size: 'original' }}
                size="medium"
                buttonType="tertiary-outline"
                onlyIcon={true}
            />
        </div>
    );
};

export default StepperInput;
